import { Button, Icon, IconProps, Stack, Tooltip } from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import classNames from 'classnames';
import { DateTime as LDateTime } from 'luxon';
import React, { useState, type JSX } from 'react';
import { NavLink } from 'react-router';

import { ApiCallDetailsModal } from './api_call_details_modal';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { emptyTableState, getTableHeaderClassNames } from '../../components/table';
import {
  ApiHealth,
  DateTime,
  CompanyWebhookSubscription,
  CompanyWebhookSubscriptionStatus,
  ImplementationStatus,
} from '../../lib/graphql';
import { Table } from '../../lib/table';
import { Routes } from '../../routes';

import styles from './styles.module.css';

export type ApiEndpointUsageLogInList = {
  api_implemented_at: DateTime | null;
  counters: {
    logDate: DateTime;
    statusCode: number | null;
    noOfCalls: number;
  }[];
  endpoint_id: string;
  endpoint_name: string;
  first_call_at: DateTime;
  last_call_at: DateTime;
  integration_identifier: string;
  webhook_subscription: CompanyWebhookSubscription | null;
  implementation_status: ImplementationStatus;
  api_health_status: ApiHealth;
};

export const getImplementationStatusLabel = (status: ImplementationStatus): string => {
  switch (status) {
    case ImplementationStatus.IMPLEMENTED:
      return 'Implemented';
    case ImplementationStatus.INACTIVE_MORE_THAN_30_DAYS:
      return 'Inactive more than 30 days';
    case ImplementationStatus.UNDER_IMPLEMENTATION:
      return 'Under implementation';
    default:
      return 'Unknown';
  }
};

export const getApiHealthLabel = (status: ApiHealth): string => {
  switch (status) {
    case ApiHealth.HEALTHY:
      return 'Healthy';
    case ApiHealth.CONCERNING:
      return 'Concerning';
    case ApiHealth.HIGH_ERROR_RATE:
      return 'High error rate';
    case ApiHealth.ALL_CALLS_FAILED:
      return 'All calls failed';
    default:
      return 'Unknown';
  }
};

export const getWebhookStatusLabel = (status: CompanyWebhookSubscriptionStatus | null): string => {
  if (!status) return 'Unknown';
  switch (status) {
    case CompanyWebhookSubscriptionStatus.WORKING:
      return 'Active';
    case CompanyWebhookSubscriptionStatus.PAUSED:
      return 'Paused';
    case CompanyWebhookSubscriptionStatus.BROKEN:
      return 'Broken';
    default:
      return 'Unknown';
  }
};

export type ApiEndpointUsageLogsListProps = {
  data: {
    company_id: string;
    company_name: string | null;
    api_endpoint_usage_logs_data: {
      api_endpoint_usage_logs: ApiEndpointUsageLogInList[];
      shipper_tours_count?: number;
      carrier_tours_count?: number;
    };
  }[];
  isSingleCompany: boolean;
  hasActiveFilters?: boolean;
};

interface UsageLogRow extends ApiEndpointUsageLogInList {
  id: string;
  company_id: string;
  company_name: string;
}

type MapDataByEndpointUsageLog = ApiEndpointUsageLogInList & {
  company_id: string;
  company_name: string;
  implementation_status: ImplementationStatus;
  api_health_status: ApiHealth;
  webhook_subscription: CompanyWebhookSubscription | null;
};

class ApiEndpointUsageLogTable extends Table<MapDataByEndpointUsageLog> {}
class EndpointUsageLogTable extends Table<{ statusCode: number; apiCalls: { [date: string]: number } }> {}

const STATUS_CODES = {
  SUCCESS: { label: 'Success', status: 'success' },
  CLIENT_ERROR: { label: 'Client Error', status: 'client-error' },
  SERVER_ERROR: { label: 'Server Error', status: 'server-error' },
  UNKNOWN: { label: 'Unknown', status: 'unknown' },
  OTHER: { label: 'Other', status: 'other' },
} as const;

const HTTP_ERROR_LABELS: Record<number, string> = {
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  429: 'Too Many Requests',
  500: 'Internal Server Error',
};

export const ApiEndpointUsageLogsList: React.FunctionComponent<ApiEndpointUsageLogsListProps> = ({
  data,
  isSingleCompany,
  hasActiveFilters = false,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    companyId: string;
    endpointId: string;
    endpointName: string;
    integrationIdentifier: string;
    date: string;
    statusCode: string;
    apiCallsCount: number;
  } | null>(null);

  const handleExpandClick = (companyId: string, endpointId: string, integrationIdentifier: string) => {
    setExpandedRows((currentState) => {
      const rowId = `${companyId}:${endpointId}:${integrationIdentifier}`;
      const newExpandedRows = new Set(currentState);
      if (newExpandedRows.has(rowId)) {
        newExpandedRows.delete(rowId);
      } else {
        newExpandedRows.add(rowId);
      }
      return newExpandedRows;
    });
  };

  const handleApiCallClick = (
    companyId: string,
    endpointId: string,
    endpointName: string,
    integrationIdentifier: string,
    date: string,
    statusCode: number,
    apiCallsCount: number,
  ) => {
    setModalState({
      isOpen: true,
      companyId,
      endpointId,
      endpointName,
      integrationIdentifier,
      date,
      statusCode: statusCode.toString(),
      apiCallsCount,
    });
  };

  const handleCloseModal = () => {
    setModalState(null);
  };

  const apiEndpointUsageLogsTableHeaderColumns: { keyPath: string; value: string | JSX.Element }[] = [
    !isSingleCompany
      ? {
          keyPath: 'company_name',
          value: 'Company',
        }
      : undefined,
    {
      keyPath: 'endpoint_name',
      value: 'Endpoint',
    },
    {
      keyPath: 'integration_identifier',
      value: 'Source',
    },
    {
      keyPath: 'first_call_at',
      value: 'First call at',
    },
    {
      keyPath: 'last_call_at',
      value: 'Last call at',
    },
    {
      keyPath: 'api_health_status',
      value: (
        <div>
          <Tooltip
            content={
              <>
                <p>
                  API health is calculated based on the success rate of the API calls in the last 3 days that have data.
                </p>
                <p>- Healthy: more than 90% of calls in the last 3 days have been successful</p>
                <p>- Concerning: 50-90% of calls in the last 3 days have been successful</p>
                <p>- High error rate: less than 50% of calls in the last 3 days have been successful</p>
                <p>- All calls failed: all calls in the last 3 days have failed</p>
              </>
            }
            placement="top">
            <Stack alignItems="center">
              API health
              <Icon name="small-info" />
            </Stack>
          </Tooltip>
        </div>
      ),
    },
    {
      keyPath: 'implementation_status',
      value: (
        <div>
          <Tooltip
            content={
              <>
                <p>A date of completion is calculated when one of the following 2 condition is met:</p>
                <p>- Streak: 7 consecutive days of use. The last of this day would be used as a completion date.</p>
                <p>
                  - Regularity: more than 15 days of use within a 21-day period. The last of this day would be used as a
                  completion date.
                </p>
              </>
            }
            placement="top">
            <Stack alignItems="center">
              Implementation status
              <Icon name="small-info" />
            </Stack>
          </Tooltip>
        </div>
      ),
    },
  ].filter(notNil);

  const mappedData: MapDataByEndpointUsageLog[] = data
    .map((company) => {
      return company.api_endpoint_usage_logs_data.api_endpoint_usage_logs?.map((apiEndpointUsageLog) => {
        return {
          company_id: company.company_id,
          company_name: company.company_name ?? company.company_id,
          ...apiEndpointUsageLog,
        };
      });
    })
    .filter(notNil)
    .flat();

  return (
    <>
      {isSingleCompany &&
        notNil(data[0]?.api_endpoint_usage_logs_data.shipper_tours_count) &&
        notNil(data[0]?.api_endpoint_usage_logs_data.carrier_tours_count) && (
          <div style={{ marginBottom: 12 }}>
            {data[0].api_endpoint_usage_logs_data.shipper_tours_count === 0 ||
            data[0].api_endpoint_usage_logs_data.carrier_tours_count === 0 ? (
              <p>
                Tours count:{' '}
                {data[0].api_endpoint_usage_logs_data.shipper_tours_count +
                  data[0].api_endpoint_usage_logs_data.carrier_tours_count}
              </p>
            ) : (
              <>
                <p>Tours count as a shipper: {data[0].api_endpoint_usage_logs_data.shipper_tours_count}</p>
                <p>Tours count as a carrier: {data[0].api_endpoint_usage_logs_data.carrier_tours_count}</p>
              </>
            )}
          </div>
        )}

      <div>
        <ApiEndpointUsageLogTable
          className="ui very basic sortable unstackable table"
          tableHeaders={{
            defaultClassName: getTableHeaderClassNames,
            columns: apiEndpointUsageLogsTableHeaderColumns,
          }}
          data={mappedData}
          defaultSortBy={{ keyPath: 'last_call_at', value: 'DESC' }}
          emptyStatePlaceholder={emptyTableState(
            hasActiveFilters ? 'No records match the selected filters' : 'Visibility API is not in use',
          )}>
          {({ row }) => {
            const {
              integration_identifier,
              endpoint_id,
              endpoint_name,
              counters,
              first_call_at,
              last_call_at,
              company_id,
              company_name,
              implementation_status,
            } = row.data;

            const allDates = new Set<string>();
            const groupedByStatusCode: { statusCode: number; apiCalls: { [index: string]: number } }[] = [];

            for (const counter of counters) {
              allDates.add(counter.logDate);
            }
            const sortedDates = Array.from(allDates)
              .sort((a, b) => a.localeCompare(b))
              .slice(-21);

            const filteredCounters = counters.filter((counter) => sortedDates.includes(counter.logDate));

            for (const counter of filteredCounters) {
              const status = counter.statusCode ?? 0;

              let statusGroup = groupedByStatusCode.find((item) => item.statusCode === status);
              if (!statusGroup) {
                statusGroup = {
                  statusCode: status,
                  apiCalls: Object.fromEntries(sortedDates.map((date) => [date, 0])),
                };
                groupedByStatusCode.push(statusGroup);
              }
              const currentCount = statusGroup.apiCalls[counter.logDate] ?? 0;
              statusGroup.apiCalls[counter.logDate] = currentCount + counter.noOfCalls;
            }

            groupedByStatusCode.sort((a, b) => {
              if (a.statusCode === 0) {
                return 1;
              }
              if (b.statusCode === 0) {
                return -1;
              }
              const aIsSuccess = a.statusCode >= 200 && a.statusCode < 300;
              const bIsSuccess = b.statusCode >= 200 && b.statusCode < 300;
              if (aIsSuccess && !bIsSuccess) {
                return -1;
              }
              if (!aIsSuccess && bIsSuccess) {
                return 1;
              }
              return a.statusCode - b.statusCode;
            });

            const rowId = `${company_id}:${endpoint_id}:${integration_identifier}`;
            const isExpanded = expandedRows.has(rowId);

            return (
              <React.Fragment key={rowId}>
                <tr>
                  {!isSingleCompany ? (
                    <td>
                      <NavLink to={Routes.Company.generatePath({ company_id })}>{company_name ?? company_id}</NavLink>
                    </td>
                  ) : undefined}
                  <td>{endpoint_name}</td>
                  <td>{integration_identifier}</td>
                  <td>
                    <FormattedDate date={new Date(first_call_at)} propsTimezone="UTC" />
                  </td>
                  <td>
                    <FormattedDate date={new Date(last_call_at)} propsTimezone="UTC" />
                  </td>
                  <td className={styles.apiHealthCell}>
                    <Stack alignItems="center" direction="row">
                      <Icon {...getApiHealthIcon(row.data.api_health_status)} />
                      <span>{getApiHealthLabel(row.data.api_health_status)}</span>
                    </Stack>
                  </td>
                  <td className={styles.statusCell}>
                    <Stack alignItems="center" direction="row">
                      <Icon {...getImplementationStatusIcon(implementation_status)} />
                      <span>{getImplementationStatusLabel(implementation_status)}</span>
                    </Stack>
                  </td>
                  <td className="right aligned">
                    <Button
                      hideLabel
                      iconStart={isExpanded ? 'small-caret-down' : 'small-caret-right'}
                      size="xsmall"
                      onClick={() => handleExpandClick(company_id, endpoint_id, integration_identifier)}>
                      {isExpanded ? 'Collapse' : 'Expand'}
                    </Button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={isSingleCompany ? 7 : 8} style={{ padding: '16px 24px', maxWidth: 0 }}>
                      <ExpandedRowDetails
                        row={{
                          ...row.data,
                          id: rowId,
                        }}
                        isSingleCompany={isSingleCompany}
                        onApiCallClick={(date, statusCode, apiCallsCount) =>
                          handleApiCallClick(
                            company_id,
                            endpoint_id,
                            endpoint_name,
                            integration_identifier,
                            date,
                            statusCode,
                            apiCallsCount,
                          )
                        }
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          }}
        </ApiEndpointUsageLogTable>
      </div>

      {modalState && (
        <ApiCallDetailsModal
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          companyId={modalState.companyId}
          endpointId={modalState.endpointId}
          endpointName={modalState.endpointName}
          integrationIdentifier={modalState.integrationIdentifier}
          date={modalState.date}
          statusCode={modalState.statusCode}
          apiCallsCount={modalState.apiCallsCount}
          useAdminQuery={!isSingleCompany}
        />
      )}
    </>
  );
};

const ExpandedRowDetails: React.FC<{
  row: UsageLogRow;
  isSingleCompany: boolean;
  onApiCallClick: (date: string, statusCode: number, apiCallsCount: number) => void;
}> = ({ row, onApiCallClick }) => {
  const { counters, api_implemented_at, webhook_subscription, company_id } = row;

  const allDates = new Set<string>();
  const groupedByStatusCode: { statusCode: number; apiCalls: { [index: string]: number } }[] = [];

  for (const counter of counters) {
    allDates.add(counter.logDate);
  }
  const sortedDates = Array.from(allDates)
    .sort((a, b) => a.localeCompare(b))
    .slice(-21);

  const filteredCounters = counters.filter((counter) => sortedDates.includes(counter.logDate));

  for (const counter of filteredCounters) {
    const status = counter.statusCode ?? 0;

    let statusGroup = groupedByStatusCode.find((item) => item.statusCode === status);
    if (!statusGroup) {
      statusGroup = {
        statusCode: status,
        apiCalls: Object.fromEntries(sortedDates.map((date) => [date, 0])),
      };
      groupedByStatusCode.push(statusGroup);
    }
    const currentCount = statusGroup.apiCalls[counter.logDate] ?? 0;
    statusGroup.apiCalls[counter.logDate] = currentCount + counter.noOfCalls;
  }

  groupedByStatusCode.sort((a, b) => {
    if (a.statusCode === 0) return 1;
    if (b.statusCode === 0) return -1;
    const aIsSuccess = a.statusCode >= 200 && a.statusCode < 300;
    const bIsSuccess = b.statusCode >= 200 && b.statusCode < 300;
    if (aIsSuccess && !bIsSuccess) return -1;
    if (!aIsSuccess && bIsSuccess) return 1;
    return a.statusCode - b.statusCode;
  });

  const formatDateHeader = (date: string): string => {
    return LDateTime.fromJSDate(new Date(date), { zone: 'utc' }).toFormat('dd/MM');
  };

  return (
    <div className={styles.expandedContent}>
      <div className={styles.apiCallsTableCell}>
        <div className={styles.apiEndpointExpandedContentHeaderCell}>
          <span className={styles.apiEndpointExpandedContentHeaderIcon}>📊</span>
          <h4 className={styles.apiEndpointExpandedContentHeader}>
            API Call Statistics — {row.endpoint_name} ({row.integration_identifier})
          </h4>
        </div>

        <div className={styles.tableWrapper}>
          <div className={styles.table}>
            <EndpointUsageLogTable
              className="ui-table compact-table"
              tableHeaders={{
                columns: [
                  { keyPath: 'date', value: 'Date' },
                  ...sortedDates.map((date) => ({
                    keyPath: date,
                    value: formatDateHeader(date),
                    className: (sortBy: 'ASC' | 'DESC' | null) => `center aligned ${getTableHeaderClassNames(sortBy)}`,
                  })),
                ],
                defaultClassName: getTableHeaderClassNames,
              }}
              data={groupedByStatusCode}
              emptyStatePlaceholder={emptyTableState('No endpoint usage logs available')}>
              {({ row: tableRow }) => (
                <tr key={tableRow.data.statusCode}>
                  <td className={styles.firstColumn}>
                    <div
                      className={`bold-text ${styles.cellContent} ${styles.status}`}
                      data-status={getStatusType(tableRow.data.statusCode)}>
                      {`API calls (${getStatusCodeDescription(tableRow.data.statusCode)})`}
                    </div>
                  </td>
                  {sortedDates.map((date) => (
                    <td key={date} className={styles.dataCell}>
                      <div className={styles.numberContainer}>
                        <span
                          className={`${styles.numberCell} ${styles.cellContent} ${tableRow.data.apiCalls[date] > 0 ? `${styles.statusCell} ${styles.statusCellClickable}` : ''}`}
                          data-status={
                            tableRow.data.apiCalls[date] > 0 ? getStatusType(tableRow.data.statusCode) : undefined
                          }
                          onClick={() => {
                            if (tableRow.data.apiCalls[date] > 0) {
                              onApiCallClick(date, tableRow.data.statusCode, tableRow.data.apiCalls[date]);
                            }
                          }}>
                          {tableRow.data.apiCalls[date]}
                        </span>
                      </div>
                    </td>
                  ))}
                </tr>
              )}
            </EndpointUsageLogTable>
          </div>
        </div>
      </div>

      {webhook_subscription !== null && (
        <div className={styles.webhooksManagementCell}>
          <div className={styles.apiEndpointExpandedContentHeaderCell}>
            <span className={styles.apiEndpointExpandedContentHeaderIcon}>📡</span>
            <h4 className={styles.apiEndpointExpandedContentHeader}>Webhook Management</h4>
          </div>
          <div className={styles.webhooksManagementContent}>
            <div className={styles.webhooksRow}>
              <span className={styles.webhooksLabel}>Status:</span>
              <div className={styles.webhooksStatus}>
                <div
                  className={classNames(
                    styles.webhooksStatusDot,
                    webhook_subscription.status === CompanyWebhookSubscriptionStatus.WORKING
                      ? styles.webhooksStatusDotWorking
                      : styles.webhooksStatusDotNotWorking,
                  )}
                />
                <span
                  className={classNames(
                    styles.webhooksStatusText,
                    webhook_subscription.status === CompanyWebhookSubscriptionStatus.WORKING
                      ? styles.webhooksStatusWorking
                      : styles.webhooksStatusNotWorking,
                  )}>
                  {getWebhookStatusLabel(webhook_subscription.status ?? null)}
                </span>
              </div>
              <NavLink
                to={Routes.CompanyWebhookSubscription.generatePath({
                  company_id,
                  webhook_subscription_id: webhook_subscription.subscriptionId,
                })}>
                <Button kind="subtle" size="small" style={{ minWidth: '120px', fontWeight: '500', marginLeft: '16px' }}>
                  Manage Webhook
                </Button>
              </NavLink>
            </div>
          </div>
        </div>
      )}

      {api_implemented_at !== null && (
        <div className={styles.implementationStatusCell}>
          <div className={styles.apiEndpointExpandedContentHeaderCell}>
            <span className={styles.apiEndpointExpandedContentHeaderIcon}>✅</span>
            <span className={styles.apiEndpointExpandedContentHeader}>Implementation Status</span>
          </div>
          <div className={styles.implementationStatusCellContent}>
            <strong>API implementation completion date:</strong>{' '}
            {webhook_subscription !== null ? (
              new Date(api_implemented_at) > new Date('2025-06-01T00:00:00Z') ? (
                <span>Before June 2025</span>
              ) : (
                <FormattedDate date={api_implemented_at} propsTimezone="UTC" />
              )
            ) : new Date(api_implemented_at) > new Date('2025-03-01T00:00:00Z') ? (
              <FormattedDate date={api_implemented_at} propsTimezone="UTC" />
            ) : (
              <span>Before March 2025</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function getApiHealthIcon(status: ApiHealth): Pick<IconProps, 'name' | 'kind'> {
  switch (status) {
    case ApiHealth.ALL_CALLS_FAILED:
      return { name: 'small-cross', kind: 'danger' };
    case ApiHealth.HIGH_ERROR_RATE:
    case ApiHealth.CONCERNING:
      return { name: 'small-alert', kind: 'warning' };
    case ApiHealth.HEALTHY:
    default:
      return { name: 'small-heart', kind: 'success' };
  }
}

function getImplementationStatusIcon(status: ImplementationStatus): Pick<IconProps, 'name' | 'kind'> {
  switch (status) {
    case ImplementationStatus.IMPLEMENTED:
      return { name: 'check', kind: 'success' };
    case ImplementationStatus.INACTIVE_MORE_THAN_30_DAYS:
      return { name: 'small-triangular-alert', kind: 'warning' };
    case ImplementationStatus.UNDER_IMPLEMENTATION:
      return { name: 'small-rocket', kind: 'highlight' };
    default:
      return { name: 'small-minus', kind: 'neutral' };
  }
}

const getStatusCodeDescription = (statusCode: number): string => {
  if (statusCode === 0) return STATUS_CODES.UNKNOWN.label;
  if (statusCode >= 200 && statusCode < 300) return STATUS_CODES.SUCCESS.label;

  const errorLabel = HTTP_ERROR_LABELS[statusCode];
  if (errorLabel) return `${statusCode} ${errorLabel}`;

  if (statusCode >= 400 && statusCode < 500) return STATUS_CODES.CLIENT_ERROR.label;
  if (statusCode >= 500) return STATUS_CODES.SERVER_ERROR.label;
  return `Status ${statusCode}`;
};

const getStatusType = (statusCode: number): string => {
  if (statusCode === 0) return STATUS_CODES.UNKNOWN.status;
  if (statusCode >= 200 && statusCode < 300) return STATUS_CODES.SUCCESS.status;
  if (statusCode >= 400 && statusCode < 500) return STATUS_CODES.CLIENT_ERROR.status;
  if (statusCode >= 500) return STATUS_CODES.SERVER_ERROR.status;
  return STATUS_CODES.OTHER.status;
};
